import React from "react";
import {
  Drawer, Box, Typography, Divider, IconButton, List, ListItem,
  ListItemText, Chip, alpha, useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import { ArchiveList } from "../../../../app/models/responseModels/knowledgehubResponse";

type Props = {
  open: boolean;
  archivedFiles: ArchiveList[];
  onClose: () => void;
  onDownload: (file: ArchiveList) => void;
};

export default function KnowledgehubArchiveDrawer({ open, archivedFiles, onClose, onDownload }: Props) {
  const theme = useTheme();

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: 450, p: 0 } }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 3, py: 2 }}>
        <Typography variant="h6" fontWeight={700}>Archived Files</Typography>
        <IconButton onClick={onClose} size="small"><CloseIcon fontSize="small" /></IconButton>
      </Box>

      <Divider />

      <Box sx={{ px: 3, py: 3, overflowY: "auto", flex: 1 }}>
        {archivedFiles.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <Typography variant="body2" color="text.secondary">No archived files</Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {archivedFiles.map((file) => {
              return (
                <ListItem
                  key={file.id}
                  sx={{
                    mb: 1.5,
                    p: 2,
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    bgcolor: alpha(theme.palette.background.paper, 0.5),
                    "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.02) },
                  }}
                  secondaryAction={
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      <IconButton
                        onClick={() => onDownload(file)}
                        size="small"
                        sx={{ color: theme.palette.info.main }}
                      >
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  }
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="body2" fontWeight={500}>{file.storagePath?.split("/").pop() ?? ""}</Typography>
                        <Chip
                          label={"V"+file.versionNumber}
                          size="small"
                          sx={{
                            bgcolor: alpha(theme.palette.info.main, 0.1),
                            color: theme.palette.info.main,
                            fontSize: "0.65rem",
                            height: 20,
                          }}
                        />
                      </Box>
                    }
                    secondary={
                      <Typography variant="caption" color="text.disabled">
                        {file.isCurrent === "true" ? "Current Version" : `Archived Version`}
                      </Typography>
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        )}
      </Box>
    </Drawer>
  );
}
